import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { Loader2, Terminal } from 'lucide-react';
import { STAFF_PROXY_TESTING_COMMAND_EXECUTIONS } from '@staff-app/graphql-client';
import {
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@staff-app/ui';
import { safeDistanceToNow, safeJsonParse } from '@/lib/safe-format';
import { commandLabel } from '../lib/activity-label';
import type { CommandExecutionRow } from '../lib/types';
import { useDrawerScopeVars } from '../../../lib/drawer-mode';

export const COMMAND_HISTORY_LIMIT = 20;

type BadgeVariant = 'success' | 'destructive' | 'warning' | 'secondary';

/** open-api COMMAND_EXECUTION_STATUS — same mapping as scio3's CommandHistoryDialog. */
const STATUS: Record<number, { label: string; variant: BadgeVariant }> = {
  0: { label: 'Submitted', variant: 'warning' },
  1: { label: 'Received', variant: 'secondary' },
  2: { label: 'Executed', variant: 'success' },
  3: { label: 'Cancelled', variant: 'secondary' },
  4: { label: 'Rejected', variant: 'destructive' },
  5: { label: 'Failed', variant: 'destructive' },
};

interface Result {
  staffProxyTestingCommandExecutions: string;
}

/**
 * More › Remote command history — port of scio3 `CommandHistoryDialog`, fed by
 * `staffProxyTestingCommandExecutions` (testing-account devices only). The result column is collapsed:
 * device replies can be long JSON blobs.
 */
export function TestingCommandHistoryDialog({
  open,
  deviceId,
  onClose,
}: {
  open: boolean;
  deviceId: string;
  onClose: () => void;
}) {
  const scope = useDrawerScopeVars();
  const { data, loading, error, refetch } = useQuery<Result>(
    STAFF_PROXY_TESTING_COMMAND_EXECUTIONS,
    {
      variables: { deviceId, first: COMMAND_HISTORY_LIMIT, ...scope },
      skip: !open || !deviceId,
      fetchPolicy: 'network-only',
    },
  );

  const rows = useMemo(
    () => safeJsonParse<CommandExecutionRow[]>(data?.staffProxyTestingCommandExecutions, []) ?? [],
    [data],
  );

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        className="flex max-h-[85vh] flex-col overflow-hidden sm:max-w-2xl"
        aria-describedby={undefined}
        data-testid="testing-command-history-dialog"
      >
        <DialogHeader variant="muted" className="mb-0">
          <DialogTitle className="flex items-center gap-2">
            <Terminal className="h-5 w-5 text-muted-foreground" />
            {'Remote command history'}
          </DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto pt-4 scroll-thin">
          {loading && rows.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-3 py-10 text-center text-sm text-muted-foreground">
              <span>
                {"Couldn't load command history. "}
                {error.message}
              </span>
              <Button variant="outline" size="sm" onClick={() => void refetch()}>
                {'Retry'}
              </Button>
            </div>
          ) : rows.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              {'No command history found'}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{'Command'}</TableHead>
                  <TableHead>{'Status'}</TableHead>
                  <TableHead>{'Result'}</TableHead>
                  <TableHead>{'When'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const status =
                    row.status != null
                      ? (STATUS[row.status] ?? {
                          label: `Unknown (${row.status})`,
                          variant: 'secondary' as const,
                        })
                      : null;
                  const at = row.lastUpdatedDate ?? row.createdAt;
                  return (
                    <TableRow key={row._id} data-testid="testing-command-history-row">
                      <TableCell className="text-sm">
                        <span className="font-medium" title={row.command ?? undefined}>
                          {commandLabel(row.command)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {status ? <Badge variant={status.variant}>{status.label}</Badge> : '—'}
                      </TableCell>
                      <TableCell className="max-w-[16rem] text-xs text-muted-foreground">
                        {row.result ? (
                          <details>
                            <summary className="cursor-pointer select-none truncate">
                              {row.result.length > 40 ? `${row.result.slice(0, 40)}…` : row.result}
                            </summary>
                            <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-all rounded bg-muted p-2 font-mono text-[11px]">
                              {row.result}
                            </pre>
                          </details>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell
                        className="whitespace-nowrap text-xs text-muted-foreground"
                        title={at ?? undefined}
                      >
                        {safeDistanceToNow(at, { addSuffix: true }, '—')}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" data-testid="testing-command-history-close">
              {'Close'}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
